import { useState, useEffect } from 'react';
import { MessageSquare, Plus, Trash2, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function Subreddits() {
  const { activeBusiness, user } = useAuth();
  const navigate = useNavigate();
  const plan = user?.plan || 'free';
  const subLimit = plan === 'free' ? 5 : 50;
  const isPaid = plan === 'pro' || plan === 'agency' || user?.role === 'admin';
  const [subreddits, setSubreddits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSub, setNewSub] = useState('');
  const [adding, setAdding] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const fetchSubreddits = async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const data = await api.getSubreddits(activeBusiness._id);
      setSubreddits(data);
    } catch {
      toast.error('Failed to load subreddits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubreddits(); }, [activeBusiness]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newSub.trim() || !activeBusiness) return;
    setAdding(true);
    try {
      await api.addSubreddit(activeBusiness._id, newSub.trim());
      setNewSub('');
      fetchSubreddits();
      toast.success('Subreddit added!');
    } catch (err) {
      toast.error(err.message || 'Failed to add subreddit');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (subId) => {
    try {
      const result = await api.toggleSubreddit(subId);
      setSubreddits(prev => prev.map(s => s._id === subId ? { ...s, enabled: result.enabled } : s));
    } catch {
      toast.error('Failed to toggle subreddit');
    }
  };

  const handleDelete = async (subId) => {
    try {
      await api.deleteSubreddit(subId);
      setSubreddits(prev => prev.filter(s => s._id !== subId));
      toast.success('Subreddit removed');
    } catch {
      toast.error('Failed to delete subreddit');
    }
  };

  const handleRescan = async () => {
    if (!activeBusiness?.website_url || !isPaid) return;
    setRescanning(true);
    try {
      const resp = await fetch('/api/scan', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: activeBusiness.website_url }) });
      if (!resp.ok) throw new Error('Rescan failed');
      const data = await resp.json();
      const newSubs = (data.suggested_subreddits || []).filter(s => !subreddits.some(x => x.name === s));
      let added = 0;
      for (const s of newSubs) {
        if (subreddits.length + added >= subLimit) break;
        try { await api.addSubreddit(activeBusiness._id, s); added++; } catch {}
      }
      toast.success(`Added ${added} new subreddits from rescan`);
      fetchSubreddits();
    } catch (err) { toast.error(err.message || 'Rescan failed'); }
    finally { setRescanning(false); }
  };

  if (!activeBusiness) {
    return (
      <DashboardLayout title="Subreddits">
        <div className="flex flex-col items-center justify-center py-20">
          <MessageSquare className="w-12 h-12 text-[#9ca3af] mb-4" />
          <p className="text-sm text-[#5B667A]">Add a business first to manage subreddits.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Subreddits">
      {/* Add subreddit */}
      <Card className="p-4 rounded-xl border-[#E6E8EE] mb-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
        <form onSubmit={handleAdd} className="flex gap-3">
          <Input
            placeholder="r/subreddit"
            value={newSub}
            onChange={(e) => setNewSub(e.target.value)}
            className="h-10 rounded-lg flex-1"
            data-testid="subreddits-new-subreddit-input"
          />
          <Button
            type="submit"
            disabled={adding || !newSub.trim()}
            className="h-10 rounded-lg font-medium text-sm"
            style={{ background: 'linear-gradient(180deg, #FF6A2A 0%, #FF4500 100%)' }}
            data-testid="subreddits-add-subreddit-button"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add Subreddit</>}
          </Button>
        </form>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-[#5B667A]">{subreddits.length}/{subLimit} subreddits</span>
          <div className="flex items-center gap-3">
            {isPaid && subreddits.length < subLimit && (
              <button onClick={handleRescan} disabled={rescanning} className="text-xs text-[#2563EB] font-semibold hover:underline flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${rescanning ? 'animate-spin' : ''}`} /> {rescanning ? 'Rescanning...' : 'Rescan website for more'}
              </button>
            )}
            {subreddits.length >= subLimit - 1 && plan === 'free' && (
              <button onClick={() => navigate('/settings')} className="text-xs text-[#FF4500] font-semibold hover:underline flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Upgrade for more
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Subreddits list */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
        </div>
      ) : subreddits.length === 0 ? (
        <Card className="p-12 rounded-xl border-[#E6E8EE] text-center">
          <MessageSquare className="w-10 h-10 text-[#9ca3af] mx-auto mb-3" />
          <h3 className="text-base font-semibold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No subreddits yet</h3>
          <p className="text-sm text-[#5B667A]">Add subreddits to monitor for relevant discussions.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {subreddits.map((sub) => (
            <Card key={sub._id} className="flex items-center justify-between px-4 py-3 rounded-xl border-[#E6E8EE]">
              <div className="flex items-center gap-3 flex-1">
                <span className={`text-sm font-medium ${sub.enabled ? 'text-[#0B0F17]' : 'text-[#9ca3af]'}`}>r/{sub.name}</span>
                {!sub.enabled && <Badge variant="secondary" className="text-xs">Disabled</Badge>}
                {sub.last_scanned && (
                  <span className="text-xs text-[#9ca3af]">Last scanned: {new Date(sub.last_scanned).toLocaleDateString()}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={sub.enabled}
                  onCheckedChange={() => handleToggle(sub._id)}
                  data-testid="subreddits-toggle"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[#9ca3af] hover:text-red-500"
                  onClick={() => handleDelete(sub._id)}
                  data-testid="subreddits-delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
